import { atlasRoutes } from "@/app/router/routes";
import { Button } from "@/components/button";
import { api } from "@/modules/api";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { FC } from "react";
import { useNavigate, useParams } from "react-router";
import styled from "@emotion/styled";
import { Uuid } from "@/domains/global/identifiers";

export const AdminMemberBadgesViewPage: FC = () => {
  const { memberBadgeId } = useParams<{ memberBadgeId: Uuid }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const result = useQuery({
    queryKey: ["member-badge", memberBadgeId],
    enabled: !!memberBadgeId,
    queryFn: () =>
      api.get("/internal/v1/staff/member-badges/{staff_member_badge_id}", {
        params: { path: { staff_member_badge_id: memberBadgeId! } },
      }),
  });

  if (!memberBadgeId) return <div>Invalid ID</div>;

  const handleDelete = async () => {
    if (confirm("Are you sure you want to remove this badge from the member?")) {
      await api.delete("/internal/v1/staff/member-badges/{staff_member_badge_id}", {
        params: { path: { staff_member_badge_id: memberBadgeId } },
      });
      queryClient.invalidateQueries({ queryKey: ["member-badges"] });
      navigate(atlasRoutes.adminMemberBadges({}).path);
    }
  };

  const badge = result.data?.data;

  return (
    <Container>
      <h1>Member Badge Details</h1>
      {badge ? (
        <Content>
          <InfoSection>
            <InfoField>
              <Label>Badge Award ID</Label>
              <Value>{badge.id}</Value>
            </InfoField>

            <InfoField>
              <Label>Staff Member ID</Label>
              <Value>{badge.staff_member_id}</Value>
            </InfoField>

            <InfoField>
              <Label>Badge ID</Label>
              <Value>{badge.staff_badge_id}</Value>
            </InfoField>

            <InfoField>
              <Label>Award Context</Label>
              <Context>{badge.context ?? ""}</Context>
            </InfoField>

            <InfoField>
              <Label>Awarded On</Label>
              <Value>{new Date(badge.created_at).toLocaleDateString()}</Value>
            </InfoField>
          </InfoSection>

          <ButtonGroup>
            <DeleteButton onClick={handleDelete}>Remove Badge from Member</DeleteButton>
          </ButtonGroup>
        </Content>
      ) : (
        <div>Loading...</div>
      )}
    </Container>
  );
};

const Container = styled.div`
  padding: 20px;
  max-width: 600px;
  margin: 0 auto;
`;

const Content = styled.div`
  display: flex;
  flex-direction: column;
  gap: 32px;
`;

const InfoSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

const InfoField = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const Label = styled.div`
  font-weight: 500;
  font-size: 14px;
  color: #666;
`;

const Value = styled.div`
  font-size: 16px;
  color: #333;
`;

const Context = styled.div`
  font-size: 16px;
  color: #333;
  padding: 12px;
  background-color: #f8f9fa;
  border-radius: 4px;
  white-space: pre-wrap;
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 12px;
  margin-top: 16px;
`;

const DeleteButton = styled(Button)`
  background-color: #dc3545;

  &:hover {
    background-color: #c82333;
  }
`;
